import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { DigiQuoteAddOn, QuoteItemDigiQuoteData } from '@frontend/api';
import {
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuDivider,
  MenuList,
  MenuItem,
  MenuGroup,
  Text,
  PencilBarIcon,
  Tooltip,
  IconButton,
  SearchInput,
  SearchInputField,
  Flex,
  CalendarIcon,
  Heading,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  InputGroup,
  InputRightAddon,
  DatePicker,
  ButtonGroup,
  VStack,
  ChainLinkIcon,
} from '@frontend/components';

import { LEAD_TIME_UNITS, LeadTimeUnit } from 'constants/all';

import { useAppSelector } from 'store/hooks';

import { toMoment } from 'utils/dateUtils';
import { pluralize } from 'utils/stringUtils';

import * as actions from '../data/actions';
import {
  getNumberOfSelectedParts,
  getFilteredQuoteItems,
  getSelectedOptions,
  getAdjustments,
} from '../data/reducer';
import {
  getLeadTimeFromShipsOnDate,
  getShipsOnDateFromLeadTime,
} from '../utils';

const useBulkSelectOptions = (quoteItems: QuoteItemDigiQuoteData[]) => {
  const dispatch = useDispatch();

  return useCallback(
    (quantityValue?: number) => {
      const nextOptions = quoteItems.reduce((acc, item) => {
        const rootComponent = item.rootComponent;
        const addOnsMap = rootComponent.addOns.reduce(
          (map, addOn) => ({ [addOn.id]: addOn, ...map }),
          {} as Record<number, DigiQuoteAddOn>,
        );
        const quantity =
          quantityValue != null
            ? rootComponent.quantities.find(
                (q) => q.quantity === quantityValue,
              )!
            : rootComponent.quantities[0];

        const nextOption = {
          quantity: quantity,
          expedite: undefined,
          addOns: rootComponent.addOnCells
            .filter(
              (addOnCell) =>
                addOnCell.quantityId === quantity.id &&
                addOnsMap[addOnCell.addOnId]?.isRequired,
            )
            .reduce(
              (addOns, addOnCell) => ({
                ...addOns,
                [addOnCell.addOnId]: addOnCell,
              }),
              {},
            ),
        };
        return { ...acc, [item.id]: nextOption };
      }, {});

      dispatch(actions.bulkSelectOption(nextOptions));
    },
    [dispatch, quoteItems],
  );
};

type BulkSelectionButtonProps = {
  isSingleQuantity: boolean;
  quoteItems: QuoteItemDigiQuoteData[];
};

const BulkSelectionButton = ({
  isSingleQuantity,
  quoteItems,
}: BulkSelectionButtonProps) => {
  const bulkSelectOptions = useBulkSelectOptions(quoteItems);

  if (isSingleQuantity) {
    return (
      <Button size="md" onClick={() => bulkSelectOptions()}>
        Select All
      </Button>
    );
  }

  const referenceQuantities = quoteItems[0].rootComponent.quantities.map(
    (q) => q.quantity,
  );

  return (
    <Menu>
      <MenuButton size="md">Select All</MenuButton>
      <MenuList>
        <MenuGroup title="At Qty Break...">
          {referenceQuantities.map((quantity) => (
            <MenuItem
              key={quantity}
              onClick={() => bulkSelectOptions(quantity)}
            >
              {quantity}
            </MenuItem>
          ))}
        </MenuGroup>
      </MenuList>
    </Menu>
  );
};

type EditSelectionButtonProps = {
  isEligibleForBulkSelection: boolean;
  isSingleQuantity: boolean;
  quoteItems: QuoteItemDigiQuoteData[];
};

const EditSelectionButton = ({
  isEligibleForBulkSelection,
  isSingleQuantity,
  quoteItems,
}: EditSelectionButtonProps) => {
  const dispatch = useDispatch();
  const bulkSelectOptions = useBulkSelectOptions(quoteItems);

  if (!isEligibleForBulkSelection) {
    return (
      <Button
        variant="link"
        size="md"
        colorScheme="onInteraction"
        onClick={() => dispatch(actions.bulkSelectOption({}))}
      >
        Deselect All
      </Button>
    );
  }

  const referenceQuantities = quoteItems[0].rootComponent.quantities.map(
    (q) => q.quantity,
  );

  return (
    <Tooltip label="Edit Selection">
      <Menu>
        <MenuButton as={IconButton} size="sm" colorScheme="onInteraction">
          <PencilBarIcon />
        </MenuButton>
        <MenuList>
          <MenuGroup
            title={isSingleQuantity ? '' : 'Select All at Qty Break...'}
          >
            {isSingleQuantity ? (
              <MenuItem onClick={() => bulkSelectOptions()}>
                Select All
              </MenuItem>
            ) : (
              referenceQuantities.map((quantity) => (
                <MenuItem
                  key={quantity}
                  onClick={() => bulkSelectOptions(quantity)}
                >
                  {quantity}
                </MenuItem>
              ))
            )}
          </MenuGroup>
          <MenuDivider />
          <MenuGroup>
            <MenuItem onClick={() => dispatch(actions.bulkSelectOption({}))}>
              Deselect All
            </MenuItem>
          </MenuGroup>
        </MenuList>
      </Menu>
    </Tooltip>
  );
};

type SelectionContentProps = {
  quoteItems: QuoteItemDigiQuoteData[];
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  onEditShipsOn: () => void;
};

const SelectionContent = ({
  quoteItems,
  searchQuery,
  setSearchQuery,
  onEditShipsOn,
}: SelectionContentProps) => {
  const dispatch = useDispatch();
  const filteredQuoteItems = useSelector(getFilteredQuoteItems);

  const isEligibleForBulkSelection = useMemo(() => {
    // We want to allow bulk selection if:
    // All quote items have no expedites
    // AND All quote items have the same number of quantities
    // AND
    //   All quote items have only one quantity
    //   OR all quote items quantities have the same values

    // Get the reference quantities (as array of numbers) from the first item
    const referenceQuantities = quoteItems[0].rootComponent.quantities.map(
      (q) => q.quantity,
    );

    // Check all quote items
    return quoteItems.every((item) => {
      const quantities = item.rootComponent.quantities;
      // Check expedites: all quantities must have expedites.length === 0
      const hasNoExpedites = quantities.every(
        (q) => !q.expedites || q.expedites.length === 0,
      );
      const sameNumberOfQuantities =
        quantities.length === referenceQuantities.length;
      const sameQuantitiesValuesOrOneQuantity =
        quantities.length === 1 ||
        (sameNumberOfQuantities &&
          quantities.every(
            (q, idx) => q.quantity === referenceQuantities[idx],
          ));
      return (
        hasNoExpedites &&
        sameNumberOfQuantities &&
        sameQuantitiesValuesOrOneQuantity
      );
    });
  }, [quoteItems]);

  const isSingleQuantity = useMemo(() => {
    return quoteItems[0].rootComponent.quantities.length === 1;
  }, [quoteItems]);

  const numberOfSelectedParts = useSelector(getNumberOfSelectedParts);
  const hints = quoteItems
    .flatMap((item) => [
      item.rootComponent.partNumber,
      item.rootComponent.part.filename,
    ])
    .filter((hint) => hint !== null);

  return (
    <>
      {numberOfSelectedParts > 0 ? (
        <>
          <Text color="feature.onInteraction" fontWeight="medium" size="md">
            {numberOfSelectedParts}/{quoteItems.length}
            {' selected'}
          </Text>
          <EditSelectionButton
            isEligibleForBulkSelection={isEligibleForBulkSelection}
            isSingleQuantity={isSingleQuantity}
            quoteItems={quoteItems}
          />
          <Button
            variant="secondary"
            size="md"
            onClick={onEditShipsOn}
            icon={<CalendarIcon />}
          >
            Edit Ships On
          </Button>
        </>
      ) : searchQuery ? (
        <Text color="feature.secondary" fontWeight="medium" size="md">
          {pluralize('result', filteredQuoteItems.length)}
        </Text>
      ) : null}
      {numberOfSelectedParts === 0 &&
      isEligibleForBulkSelection &&
      !searchQuery ? (
        <BulkSelectionButton
          isSingleQuantity={isSingleQuantity}
          quoteItems={quoteItems}
        />
      ) : null}
      <Flex flex={1} justifyContent="flex-end">
        <SearchInput
          width={30}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            dispatch(actions.setSearchQuery({ searchQuery: e.target.value }));
          }}
          onClear={() => {
            setSearchQuery('');
            dispatch(actions.setSearchQuery({ searchQuery: '' }));
          }}
        >
          <SearchInputField
            placeholder="Search by part # or filename"
            hints={hints}
          />
        </SearchInput>
      </Flex>
    </>
  );
};

type ShipsOnEditorProps = {
  leadTimeDisplayUnit: LeadTimeUnit;
  onClose: () => void;
};

const ShipsOnEditor = ({
  leadTimeDisplayUnit,
  onClose,
}: ShipsOnEditorProps) => {
  const dispatch = useDispatch();
  const selectedOptions = useAppSelector(getSelectedOptions);
  const adjustments = useAppSelector(getAdjustments);
  const optionLeadTimes = Object.values(selectedOptions).map(
    (option) =>
      option.manualLeadTime ??
      option.expedite?.leadTime ??
      option.quantity.leadTime,
  );
  const optionShipsOns = Object.values(selectedOptions).map(
    (option) => option.shipsOn,
  );
  const adjustmentLeadTimes = Object.values(adjustments).map(
    (adjustment) => adjustment.leadTime || 0,
  );
  const adjustmentShipsOns = Object.values(adjustments).map(
    (adjustment) => adjustment.shipsOn || null,
  );
  const leadTimes = [...optionLeadTimes, ...adjustmentLeadTimes];
  const shipsOns = [...optionShipsOns, ...adjustmentShipsOns];
  const defaultLeadTime = leadTimes.every((time) => time === leadTimes[0])
    ? leadTimes[0]
    : 0;
  const defaultShipsOn = shipsOns.every((shipsOn) => shipsOn === shipsOns[0])
    ? shipsOns[0]
    : null;

  const [leadTime, setLeadTime] = useState(defaultLeadTime);
  const [shipsOn, setShipsOn] = useState(defaultShipsOn);

  const onSave = useCallback(() => {
    dispatch(actions.setLeadTimeBulk({ leadTime, shipsOn }));
    onClose();
  }, [onClose, dispatch, leadTime, shipsOn]);

  return (
    <VStack as="form" width="100%" spacing={1.5} alignItems="stretch">
      <Heading as="h4" size="md">
        Edit Lead Time/Ships On Date
      </Heading>
      <HStack spacing={1}>
        <FormControl>
          <FormLabel>Lead Time</FormLabel>
          <InputGroup>
            <NumberInput
              value={leadTime}
              onChange={(_, val) => {
                setLeadTime(isNaN(val) ? 0 : Math.max(val, 0));
                setShipsOn(null);
              }}
              hideIncrementStepper
              hideDecrementStepper
            >
              <NumberInputField textAlign="right" paddingRight={1} />
            </NumberInput>
            <InputRightAddon>
              {LEAD_TIME_UNITS[leadTimeDisplayUnit].unitName.toLowerCase()}s
            </InputRightAddon>
          </InputGroup>
        </FormControl>
        <ChainLinkIcon mt={3} boxSize={2.5} color="feature.secondary" />
        <FormControl>
          <FormLabel>Ships On Date</FormLabel>
          <DatePicker
            style={{ width: '100%' }}
            allowClear={false}
            disabledDate={(current) => current.isBefore(moment(), 'day')}
            value={
              shipsOn
                ? toMoment(shipsOn)
                : toMoment(
                    getShipsOnDateFromLeadTime({
                      fromDate: new Date(),
                      leadTime,
                      leadTimeDisplayUnit,
                    }).toISOString(),
                  )
            }
            onChange={(newMoment) => {
              if (newMoment) {
                setLeadTime(
                  getLeadTimeFromShipsOnDate({
                    fromDate: new Date(),
                    shipsOnDate: newMoment.toDate(),
                    leadTimeDisplayUnit,
                  }),
                );
                setShipsOn(newMoment.format('YYYY-MM-DD'));
              } else {
                setLeadTime(0);
                setShipsOn(null);
              }
            }}
          />
        </FormControl>
      </HStack>
      <ButtonGroup justifyContent="flex-end">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          isDisabled={
            leadTime === defaultLeadTime && shipsOn === defaultShipsOn
          }
          onClick={onSave}
        >
          Save
        </Button>
      </ButtonGroup>
    </VStack>
  );
};

export type MultiplePartsHeaderProps = {
  quoteItems: QuoteItemDigiQuoteData[];
  leadTimeDisplayUnit: LeadTimeUnit;
};

export const MultiplePartsHeader = ({
  quoteItems,
  leadTimeDisplayUnit,
}: MultiplePartsHeaderProps) => {
  const numberOfSelectedParts = useSelector(getNumberOfSelectedParts);

  const [isEditingShipsOn, setIsEditingShipsOn] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <HStack
      paddingY={1.5}
      paddingX={2}
      borderBottom="1px solid"
      borderColor="separator.divider"
      backgroundColor={
        numberOfSelectedParts > 0 && !isEditingShipsOn
          ? 'interaction.active'
          : 'transparent'
      }
    >
      {isEditingShipsOn ? (
        <ShipsOnEditor
          leadTimeDisplayUnit={leadTimeDisplayUnit}
          onClose={() => setIsEditingShipsOn(false)}
        />
      ) : (
        <SelectionContent
          quoteItems={quoteItems}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          onEditShipsOn={() => setIsEditingShipsOn(true)}
        />
      )}
    </HStack>
  );
};
